import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  Button,
  useTheme,
  Chip,
  Stack,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Paper,
} from '@mui/material';
import {
  ArrowForward,
  CheckCircle,
  School,
  HealthAndSafety,
  Home,
  Computer,
  Flight,
  Schedule,
  Business,
  Router,
  CellTower,
  Public,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import CTASection from '../components/common/CTASection';

const Careers = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);

  // Stats matching the correct data
  const stats = [
    { number: '50,000+', label: 'KM of Fiber Network', icon: <Router />, color: '#e60108' },
    { number: '100+', label: 'Telecom Towers', icon: <CellTower />, color: '#191919' },
    { number: '15+', label: 'Countries Served', icon: <Public />, color: '#e60108' },
    { number: '100%', label: 'Uptime Guarantee', icon: <CheckCircle />, color: '#191919' },
  ];

  const benefits = [
    { icon: <HealthAndSafety />, title: 'Health Insurance', desc: 'Comprehensive medical, dental, and vision coverage', color: '#e60108' },
    { icon: <Home />, title: 'Remote Work', desc: 'Flexible work arrangements worldwide', color: '#191919' },
    { icon: <School />, title: 'Learning & Development', desc: '$5,000 annual learning budget', color: '#e60108' },
    { icon: <Computer />, title: 'Tech Setup', desc: 'Latest Mac or PC with accessories', color: '#a0a0a0' },
    { icon: <Flight />, title: 'Global Travel', desc: 'Opportunity to work from global offices', color: '#e60108' },
    { icon: <Schedule />, title: 'Flexible Hours', desc: 'Work-life balance priority', color: '#191919' },
  ];

  const handleApplyClick = () => {
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  return (
    <Box>
      {/* Hero Section with Image */}
      <Box
        sx={{
          position: 'relative',
          height: '50vh',
          minHeight: 400,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          overflow: 'hidden',
        }}
      >
        <Box
          component="img"
          src="/images/Join Our Team.jpg"
          alt="Join Our Team"
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 0,
          }}
        />
        
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.5) 100%)',
            zIndex: 1,
          }}
        />
        
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Chip
              label="Careers"
              icon={<Business sx={{ fontSize: 16 }} />}
              sx={{
                bgcolor: '#e60108',
                color: '#fefefe',
                mb: 3,
                fontWeight: 700,
                letterSpacing: '0.5px',
                mx: 'auto',
                '& .MuiChip-icon': { color: '#fefefe' },
              }}
            />
            <Typography
              variant="h1"
              sx={{
                color: '#fefefe',
                mb: 2,
                fontSize: { xs: '2.5rem', md: '4rem' },
                fontWeight: 800,
                lineHeight: 1.2,
                textShadow: '0 2px 20px rgba(0,0,0,0.3)',
              }}
            >
              Join Our Team
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'rgba(254,254,254,0.9)',
                maxWidth: 700,
                mx: 'auto',
                fontSize: '1.25rem',
                lineHeight: 1.6,
              }}
            >
              Shape the future of global communication with us across 5+ countries worldwide
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Stats Section with Future Goal Note */}
      <Box sx={{ py: 6, bgcolor: '#fefefe' }}>
        <Container maxWidth="xl">
          <Grid container spacing={3} justifyContent="center">
            {stats.map((stat, index) => (
              <Grid item xs={6} sm={3} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card sx={{ p: 3, textAlign: 'center', borderRadius: 3 }}>
                    <Avatar sx={{ bgcolor: stat.color + '15', color: stat.color, margin: '0 auto 16px', width: 60, height: 60 }}>
                      {stat.icon}
                    </Avatar>
                    <Typography variant="h3" fontWeight="bold" sx={{ color: stat.color, fontSize: { xs: '1.5rem', md: '2rem' } }}>
                      {stat.number}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                      {stat.label}
                    </Typography>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>

          {/* Single Future Goal Note */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            viewport={{ once: true }}
          >
            <Paper
              elevation={0}
              sx={{
                mt: 5,
                p: 3,
                bgcolor: '#f8f8f8',
                borderRadius: 3,
                textAlign: 'center',
                maxWidth: 700,
                mx: 'auto',
                border: '1px solid #e6010820',
              }}
            >
              <Typography variant="body1" sx={{ color: '#191919' }}>
                <Box component="span" sx={{ color: '#e60108', fontWeight: 700 }}>🎯 Future Goal:</Box> These statistics represent our ambitious targets for the coming years as we continue to expand and enhance our global communication infrastructure.
              </Typography>
            </Paper>
          </motion.div>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Box sx={{ py: 10, bgcolor: '#fefefe' }}>
        <Container maxWidth="xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            style={{ textAlign: 'center' }}
          >
            <Chip
              label="Why Join Us"
              sx={{ mb: 2, bgcolor: '#e6010810', color: '#e60108', fontWeight: 600, mx: 'auto', display: 'table' }}
            />
            <Typography variant="h2" sx={{ mb: 2, fontWeight: 800, color: '#191919' }}>
              Why Join Us?
            </Typography>
            <Typography variant="h6" sx={{ color: '#a0a0a0', mb: 6, maxWidth: 700, mx: 'auto' }}>
              Amazing benefits for amazing people
            </Typography>
          </motion.div>

          <Grid container spacing={3} justifyContent="center">
            {benefits.map((benefit, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -8 }}
                >
                  <Card sx={{ p: 3, textAlign: 'center', height: '100%', borderRadius: 3 }}>
                    <Avatar
                      sx={{
                        width: 60,
                        height: 60,
                        bgcolor: benefit.color + '15',
                        color: benefit.color,
                        margin: '0 auto 16px',
                      }}
                    >
                      {benefit.icon}
                    </Avatar>
                    <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ color: '#191919' }}>
                      {benefit.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                      {benefit.desc}
                    </Typography>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Join Our Talent Community CTA */}
      <Box sx={{ py: 10, bgcolor: '#f8f8f8' }}>
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            style={{ textAlign: 'center' }}
          >
            <Card sx={{ p: 5, borderRadius: 4, background: 'linear-gradient(135deg, #e60108 0%, #191919 100%)', color: '#fefefe' }}>
              <Typography variant="h3" gutterBottom fontWeight="800">
                Join Our Talent Community
              </Typography>
              <Typography variant="h6" sx={{ mb: 4, opacity: 0.9, maxWidth: 500, mx: 'auto' }}>
                Don't see the perfect role? Send us your resume and we'll keep you in mind for future opportunities.
              </Typography>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForward />}
                  onClick={handleApplyClick}
                  sx={{
                    bgcolor: '#fefefe',
                    color: '#e60108',
                    px: 5,
                    py: 1.5,
                    '&:hover': {
                      bgcolor: '#fefefe',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  Send Your Resume
                </Button>
              </motion.div>
            </Card>
          </motion.div>
        </Container>
      </Box>

      <CTASection 
        title="Questions About Careers?" 
        subtitle="Contact our HR team to learn more about working with us"
        buttonText="Contact HR"
        buttonLink="/contact"
      />

      {/* Application Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: '#e60108', color: '#fefefe' }}>
          <Typography variant="h6" fontWeight="bold">Join Our Talent Community</Typography>
          <Typography variant="body2" sx={{ color: '#fefefe', opacity: 0.9 }}>
            Send us your resume and we'll contact you when opportunities arise
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField fullWidth label="Full Name" required />
            <TextField fullWidth label="Email" type="email" required />
            <TextField fullWidth label="Phone" required />
            <TextField fullWidth label="LinkedIn Profile" />
            <TextField
              fullWidth
              label="Tell us about your skills and experience"
              multiline
              rows={4}
              placeholder="Share your background, expertise, and what role you're looking for..."
            />
            <Button 
              variant="outlined" 
              component="label" 
              sx={{ 
                borderColor: '#e60108', 
                color: '#e60108',
                '&:hover': { borderColor: '#b80006', bgcolor: '#e6010810' }
              }}
            >
              Upload Resume (PDF, DOC)
              <input type="file" hidden accept=".pdf,.doc,.docx" />
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button onClick={handleCloseDialog} sx={{ color: '#a0a0a0' }}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleCloseDialog} 
            sx={{ bgcolor: '#e60108', '&:hover': { bgcolor: '#b80006' } }}
          >
            Submit Application
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Careers;